import * as React from 'react';
import { useState } from 'react';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { getPhoneAndCode, getUKPhoneWithoutFirstZero } from '../../../../../helpers/phone/phone';
import { Button } from '../../../../../components/Button/Button';
import { PHONE_CODE } from '../../../../../consts/common';
import { checkAvailability } from '../../../../../helpers/service/public/register';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleExclamation } from '@fortawesome/free-solid-svg-icons';
import '../ContactChangeForm.scss';

interface Props {
  phone: string;
  onSubmit: (data: any) => void;
  onCancel: () => void;
  cooldownSec?: number;
  formatMmSs?: (s: number) => string;
  actionError?: string;
  cooldownCode?: string;
  onCooldownFinished?: () => void;
}

function CooldownInlineAlert(props: {
  text: string;
  code?: string;
  cooldownSec: number;
  formatMmSs?: (s: number) => string;
  onCooldownFinished?: () => void;
}) {
  const { text, code, cooldownSec, formatMmSs, onCooldownFinished } = props;
  const [sec, setSec] = React.useState<number>(Math.max(0, Math.ceil(cooldownSec)));

  React.useEffect(() => {
    setSec(Math.max(0, Math.ceil(cooldownSec)));
  }, [cooldownSec]);

  React.useEffect(() => {
    if (sec <= 0) return;

    const id = window.setInterval(() => {
      setSec(prev => {
        const next = prev - 1;
        if (next <= 0) {
          window.clearInterval(id);
          onCooldownFinished && onCooldownFinished();
          return 0;
        }
        return next;
      });
    }, 1000);

    return () => window.clearInterval(id);
  }, [sec]);

  const isBlocked = code === 'AUTO_BLOCKED';
  const title = isBlocked ? 'Temporarily unavailable' : 'Please wait a moment';

  return (
    <div className={`eModalCooldownAlert ${isBlocked ? 'mBlocked' : 'mRateLimited'}`}>
      <div className="eModalCooldownAlertLeft">
        <div className="eModalCooldownAlertIcon">
          <FontAwesomeIcon icon={faCircleExclamation} />
        </div>
        <div className="eModalCooldownAlertText">
          <div className="eModalCooldownAlertTitle">{title}</div>
          <div className="eModalCooldownAlertMessage">{text}</div>
        </div>
      </div>

      <div className="eModalCooldownAlertTimer">{formatMmSs ? formatMmSs(sec) : `${sec}s`}</div>
    </div>
  );
}

export function PhoneChangeForm(props: Props) {
  const {
    onSubmit,
    onCancel,
    phone,

    cooldownSec,
    formatMmSs,
    actionError,
    cooldownCode,
    onCooldownFinished
  } = props;

  const phoneObj = getPhoneAndCode(phone);
  const [phoneCode, setPhoneCode] = useState(phoneObj.code);

  const isCooldown = (cooldownSec || 0) > 0;

  const turnstileTokenRef = React.useRef<string | undefined>(undefined);
  const turnstileContainerRef = React.useRef<HTMLDivElement>(null);
  const turnstileWidgetIdRef = React.useRef<string | undefined>(undefined);
  const [captchaReady, setCaptchaReady] = React.useState(false);

  React.useEffect(() => {
    const sitekey = (window as any).turnstileSiteKey;
    if (!sitekey || !turnstileContainerRef.current) return;

    const renderWidget = () => {
      if (!(window as any).turnstile || turnstileWidgetIdRef.current !== undefined) return;
      turnstileWidgetIdRef.current = (window as any).turnstile.render(turnstileContainerRef.current, {
        sitekey,
        callback: (token: string) => {
          turnstileTokenRef.current = token;
          setCaptchaReady(true);
        },
        'expired-callback': () => {
          turnstileTokenRef.current = undefined;
          setCaptchaReady(false);
        },
        'error-callback': () => {
          turnstileTokenRef.current = undefined;
          setCaptchaReady(false);
        }
      });
    };

    if ((window as any).turnstile) renderWidget();
    else (window as any).__pendingTurnstileInit = renderWidget;

    return () => {
      if ((window as any).__pendingTurnstileInit === renderWidget) (window as any).__pendingTurnstileInit = undefined;
      if (turnstileWidgetIdRef.current !== undefined && (window as any).turnstile) {
        (window as any).turnstile.remove(turnstileWidgetIdRef.current);
        turnstileWidgetIdRef.current = undefined;
      }
    };
  }, []);

  const PhoneSchema = Yup.object().shape({
    phone: Yup.string()
      .required('Required')
      .test('phone', 'Duplicate phone or incorrect symbol', function(value) {
        const phoneRegExp = /^\d+$|^\d+#\d+$/;
        const phoneNoZero = getUKPhoneWithoutFirstZero(value, phoneCode);
        const result = phoneRegExp.test(phoneNoZero);
        const phoneForCheck = `${phoneCode}${phoneNoZero}`;

        return result ? checkAvailability({ phone: phoneForCheck }).then(res => res.isAvailable) : false;
      })
      .test('phone', 'Please enter a valid mobile phone number', value => {
        if (value && value.length < 7) return false;
        return true;
      })
  });

  return (
    <Formik
      initialValues={{ phone: phoneObj.phone, phoneCode }}
      validationSchema={PhoneSchema}
      validateOnBlur={false}
      validateOnChange={false}
      onSubmit={values => {
        if (isCooldown) return;
        const captchaToken = turnstileTokenRef.current;
        turnstileTokenRef.current = undefined;
        setCaptchaReady(false);
        onSubmit({ ...values, captchaToken });
        if (turnstileWidgetIdRef.current !== undefined) {
          (window as any).turnstile?.reset(turnstileWidgetIdRef.current);
        }
      }}
    >
      {({ handleSubmit, setFieldValue, touched, errors }) => (
        <Form className="eChangeModalForm">
          <div className="eChangeModalHeader">
            <div className="eChangeModalTitle">Update phone number</div>
            <div className="eChangeModalSubtitle">We’ll send a new SMS verification code to your updated number.</div>
          </div>

          {actionError && isCooldown && (
            <CooldownInlineAlert
              text={actionError}
              code={cooldownCode}
              cooldownSec={cooldownSec || 0}
              formatMmSs={formatMmSs}
              onCooldownFinished={onCooldownFinished}
            />
          )}

          <div className="eChangeModalPhoneRow">
            <div className="form-group eChangeModalField eChangeModalCodeField">
              <label className="eChangeModalLabel">Code</label>
              <Field
                name="phoneCode"
                component="select"
                className="form-control"
                onChange={event => {
                  setPhoneCode(event.target.value);
                  setFieldValue('phoneCode', event.target.value);
                }}
                disabled={isCooldown}
              >
                <option value={PHONE_CODE.UK}>{PHONE_CODE.UK}</option>
                <option value={PHONE_CODE.US}>{PHONE_CODE.US}</option>
                <option value={PHONE_CODE.AE}>{PHONE_CODE.AE}</option>
                <option value={PHONE_CODE.DE}>{PHONE_CODE.DE}</option>
              </Field>
            </div>

            <div className="form-group eChangeModalField">
              <label className="eChangeModalLabel">Mobile number</label>
              <Field name="phone" placeholder="Mobile number" className={'form-control'} disabled={isCooldown} />
              {touched.phone && errors.phone && <div className="eChangeModalError">{String(errors.phone)}</div>}

              {isCooldown && (
                <div className="eChangeModalHint">
                  You can try again in <strong>{formatMmSs ? formatMmSs(cooldownSec || 0) : `${cooldownSec}s`}</strong>.
                </div>
              )}
            </div>
          </div>

          <div ref={turnstileContainerRef} className="eChangeTurnstile mt-3" />

          <div className="eChangeModalActions">
            <Button onClick={onCancel} text={'Cancel'} customClass={'btn-secondary'} />
            <Button
              onClick={handleSubmit}
              text={isCooldown ? 'Please wait…' : 'Save'}
              customClass={'btn btn-primary'}
              type={'submit'}
              disabled={isCooldown || !captchaReady}
            />
          </div>
        </Form>
      )}
    </Formik>
  );
}
